import {
  Button,
  Input,
  InputAmount,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { Box, Group, Modal, Text, Tooltip, rem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  type ReservationAdjustmentAddInputs,
  useReservationAdjustmentMutation,
} from '../_hooks/useReservationAdjustmentMutation';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { ReservationLineTypeAddModal } from '../../../views/accounts/line-type-mappings/reservation-line-types/ReservationLineTypeAddModal';
import { useReservationLineMappingsInputOptions } from '@vrplatform/data';
import { useTeam } from '@vrplatform/data';
import { ReservationPostingDateSelectInput } from './ReservationPostingDateSelectInput';
import { getPostingDatePlaceholder } from './ReservationPostingDateSelectInput.utils';

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

type Props = {
  reservation: {
    id: string;
    status: Reservation['status'];
    lock: Reservation['lock'];
    bookingPlatform: Reservation['bookingPlatform'];
    bookedAt?: Reservation['bookedAt'];
    checkIn?: Reservation['checkIn'];
    checkOut?: Reservation['checkOut'];
  };
  closeModal: () => void;
  reservationLine?:
    | (Omit<ReservationAdjustmentAddInputs, 'forcePostingAt'> & {
        id?: string;
        type: 'adjustment' | 'financial';
      })
    | null;
  zIndex?: number;
};

export const ReservationFinancialsAddModal = ({
  reservation,
  closeModal: cl,
  reservationLine,
  zIndex,
}: Props) => {
  const [team] = useTeam();
  const defaultRevenueRecognition = team?.defaultRevenueRecognition;

  const opened = !!reservationLine;
  const [addLineTypeModalOpened, setAddLineTypeModalOpened] = useState(false);

  const [deb] = useDebouncedValue(reservationLine, 500);
  const debounced = deb ?? reservationLine;
  const isEditMode = !!reservationLine?.id;

  const { id: reservationId } = reservation;

  const isCancelled = reservation.status === 'canceled';

  const methods = useForm<ReservationAdjustmentAddInputs>({
    defaultValues: {
      centTotal: undefined,
      description: '',
      lineType: undefined,
      party: undefined,
      postingAt: null,
    },
  });

  const lineType = useWatch({
    control: methods.control,
    name: 'lineType',
  });

  const lineMappingsQuery = useReservationLineMappingsInputOptions({
    skip: !opened,
    reservation: {
      bookingPlatform: reservation.bookingPlatform,
    },
    showSourceGroups: false,
  });
  const lineTypeOptions = lineMappingsQuery.data;

  const selectedLineType = useMemo(() => {
    return lineTypeOptions.find((option) => option.value === lineType) || null;
  }, [lineType, lineTypeOptions]);
  const selectedLineTypeLabel = selectedLineType?.label ?? lineType ?? '';

  useEffect(() => {
    methods.reset(
      reservationLine
        ? {
            centTotal: reservationLine.centTotal,
            description: reservationLine.description,
            lineType: reservationLine.lineType,
            party: reservationLine.party,
            postingAt: reservationLine.postingAt ?? null,
          }
        : {
            centTotal: undefined,
            description: '',
            lineType: undefined,
            party: undefined,
            postingAt: null,
          }
    );
  }, [methods, reservationLine]);

  const postingDatePlaceholder = useMemo(() => {
    const selectedLineTypeMapping = lineType
      ? (lineMappingsQuery.originalData ?? []).find(
          (mapping) => mapping.type === lineType
        )
      : undefined;

    const selectedChannelMapping = reservation.bookingPlatform
      ? selectedLineTypeMapping?.channelMappings.find(
          (channelMapping) =>
            channelMapping.bookingChannel === reservation.bookingPlatform
        )
      : undefined;

    const revRec =
      selectedChannelMapping?.revenueRecognition ??
      selectedLineTypeMapping?.revenueRecognition ??
      defaultRevenueRecognition;

    return getPostingDatePlaceholder(revRec);
  }, [
    defaultRevenueRecognition,
    lineMappingsQuery.originalData,
    lineType,
    reservation.bookingPlatform,
  ]);

  const closeModal = () => {
    cl();
    methods.reset({
      centTotal: undefined,
      description: '',
      lineType: undefined,
      party: undefined,
      postingAt: null,
    });
  };

  const { submit } = useReservationAdjustmentMutation(
    reservationId,
    closeModal
  );

  const submitType =
    debounced?.type === 'financial' ? 'financial' : 'adjustment';
  const title = isEditMode
    ? `Edit ${debounced?.party === 'owners' ? 'financial' : 'fee'} adjustment`
    : `Add ${debounced?.party === 'owners' ? 'financial' : 'fee'} ${submitType === 'adjustment' ? 'adjustment' : ''}`;

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={title}
      styles={{
        title: { fontWeight: 500 },
      }}
      zIndex={zIndex}
      centered
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Controller
          control={methods.control}
          rules={{
            required: 'Description is required',
            validate: trapSpacesForRequiredFields,
          }}
          name="description"
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Description"
              error={fieldState.error?.message}
              required
            >
              <Input
                value={field.value}
                setValue={field.onChange}
                error={!!fieldState.error}
                placeholder={
                  debounced?.party === 'owners'
                    ? 'Cleaning Fee Adjustment'
                    : 'Fee Adjustment'
                }
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={methods.control}
          rules={{
            required: isEditMode
              ? false
              : isCancelled && submitType === 'adjustment'
                ? false
                : 'Please select a type',
          }}
          name="lineType"
          render={({ field, fieldState }) => {
            if (isEditMode) {
              return (
                <InputWrapper
                  label="Line type"
                  error={fieldState.error?.message}
                >
                  <Input
                    value={selectedLineTypeLabel}
                    setValue={() => {}}
                    disabled
                  />
                </InputWrapper>
              );
            }

            const canCreateNewLineType =
              submitType === 'adjustment' && !reservationLine?.lineType;

            return (
              <>
                <ReservationLineTypeAddModal
                  opened={addLineTypeModalOpened}
                  closeModal={() => setAddLineTypeModalOpened(false)}
                  onCreated={(created) => {
                    field.onChange(created.type);
                    lineMappingsQuery.refetch();
                  }}
                />
                <InputWrapper
                  label={
                    <Group gap={rem(8)} display="inline-flex">
                      <Text fw={500} span>
                        Type
                      </Text>
                      {isCancelled && (
                        <Tooltip
                          label="Selecting a type is optional on cancelled reservations. The underlying account for the adjustment will default to the cancellation revenue account assignment in case no type is selected."
                          withArrow
                          maw={400}
                          multiline
                        >
                          <Icon icon="InfoIcon" size={14} />
                        </Tooltip>
                      )}
                    </Group>
                  }
                  error={fieldState.error?.message}
                  required
                >
                  <InputSelect
                    value={selectedLineType}
                    setValue={(value) => field.onChange(value?.value ?? null)}
                    type="single"
                    inputProps={{
                      error: !!fieldState.error,
                      placeholder: 'Select type',
                      withClearButton: true,
                      disabled: !!reservationLine?.lineType,
                    }}
                    data={{
                      options: lineTypeOptions,
                      loading: lineMappingsQuery.isLoading,
                      error: lineMappingsQuery.error,
                      sort: null,
                    }}
                    customBottomActions={
                      canCreateNewLineType
                        ? [
                            {
                              id: 'create-new-line-type',
                              label: 'Create new line type',
                              icon: <Icon icon="PlusIcon" size={16} />,
                              onSubmit: () => setAddLineTypeModalOpened(true),
                            },
                          ]
                        : undefined
                    }
                  />
                </InputWrapper>
              </>
            );
          }}
        />

        {reservation.lock?.booksClosed?.date && (
          <ReservationPostingDateSelectInput
            control={methods.control}
            name="postingAt"
            placeholder={postingDatePlaceholder}
            reservationDates={{
              bookedAt: reservation.bookedAt,
              checkIn: reservation.checkIn,
              checkOut: reservation.checkOut,
            }}
            forcePostingDate={reservation.lock?.status === 'locked'}
            minDate={reservation.lock?.booksClosed?.date}
            description="This date defines the journal entry date for this financial"
          />
        )}

        <Controller
          control={methods.control}
          rules={{
            validate: (value) => {
              if (typeof value !== 'number') {
                return 'Please enter a number';
              }

              if (value === 0) {
                return 'Please enter a non-zero amount';
              }

              return true;
            },
          }}
          name="centTotal"
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Amount"
              error={fieldState.error?.message}
              required
            >
              <InputAmount
                value={
                  field.value === undefined
                    ? ''
                    : Number(field.value || '0') / 100
                }
                setValue={(value) => field.onChange(Number(value || 0) * 100)}
                decimalScale={2}
                error={!!fieldState.error?.message}
                placeholder="0.00"
                leftSection={<span>$</span>}
              />
            </InputWrapper>
          )}
        />

        {!reservation.lock?.booksClosed?.date && (
          <ReservationPostingDateSelectInput
            control={methods.control}
            name="postingAt"
            placeholder={postingDatePlaceholder}
            reservationDates={{
              bookedAt: reservation.bookedAt,
              checkIn: reservation.checkIn,
              checkOut: reservation.checkOut,
            }}
            forcePostingDate={reservation.lock?.status === 'locked'}
            minDate={reservation.lock?.booksClosed?.date}
          />
        )}

        <Group justify="right" mt="md">
          <Button
            onClick={closeModal}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={methods.handleSubmit(
              async (data) =>
                await submit(
                  reservationLine?.type ?? 'adjustment',
                  isEditMode && reservationLine?.id
                    ? {
                        data,
                        lineId: reservationLine.id,
                        type: 'edit',
                      }
                    : {
                        data,
                        type: 'add',
                      }
                ),
              (err) => console.error(err)
            )}
            loading={methods.formState.isSubmitting}
          >
            {isEditMode
              ? 'Save adjustment'
              : `Add ${debounced?.type === 'adjustment' ? 'adjustment' : 'financial'}`}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
